/**
 * Low-level API client for global search (ADR 10.36).
 *
 * Calls bp-api: GET /bp/v1/search?q=&limit=
 */

import { AxiosInstance } from "axios";

/** A field that matched the term. A hit lists all of them; see `matchedOn` on each hit type. */
export type SearchMatchField =
  | "id"
  | "externalId"
  | "name"
  | "clientName"
  | "brokerName"
  | "analyst";

export interface ClientSearchHit {
  /** Client UUID used by every other bp-api client endpoint. */
  readonly id: string;
  /** broker.client.id — the value the portal's /clients/:id route takes. */
  readonly numericId: number;
  readonly name: string;
  readonly externalId?: string;
  readonly brokerName: string;
  /**
   * Every field that matched, displayed fields first (clients: name,
   * brokerName; RFPs: clientName, brokerName, analyst). Never empty.
   */
  readonly matchedOn: readonly SearchMatchField[];
}

export interface RfpSearchHit {
  /** BP numeric RFP id used in /rfps/:id. */
  readonly id: number;
  readonly uuid?: string;
  readonly clientName: string;
  readonly brokerName: string;
  readonly status?: string;
  /** Coverage effective date, RFC3339 UTC. Absent when the RFP has none. */
  readonly effectiveDate?: string;
  readonly assignedAnalyst?: string;
  /**
   * Every field that matched, displayed fields first (clients: name,
   * brokerName; RFPs: clientName, brokerName, analyst). Never empty.
   */
  readonly matchedOn: readonly SearchMatchField[];
}

export interface SearchResponse {
  readonly clients: readonly ClientSearchHit[];
  readonly rfps: readonly RfpSearchHit[];
}

/** Shortest term bp-api accepts; shorter input must not be sent. */
export const SEARCH_MIN_TERM_LENGTH = 3;
/** Rows per group the typeahead asks for. */
export const SEARCH_DEFAULT_LIMIT = 5;

export const search = async (
  term: string,
  axiosInstance: AxiosInstance,
  limit: number = SEARCH_DEFAULT_LIMIT,
  signal?: AbortSignal,
): Promise<SearchResponse> => {
  const response = await axiosInstance.get<SearchResponse>("/bp/v1/search", {
    params: { q: term, limit },
    signal,
  });

  return response.data;
};
